/**
 * JobBoardOverview — the cockpit strip's view of an INSTALL job on the board:
 * what the installer + office need at a glance (equipment, accessories, and the
 * job & install notes) plus a jump to the full job workspace. Rendered for
 * Pegasus install visits (visit_role='install' with a job_id); other tickets
 * keep the dispatch-level PegasusJobDetailBody.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight, Package, MapPin, CalendarClock, User, X } from "lucide-react";
import { useJob, useJobEquipment, type JobEquipment } from "@/features/jobs/jobs";
import { InternalNotes } from "@/lib/internalNotes";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useActiveTechs } from "./api";
import type { PendingTicket } from "./queue";

const unitLabel = (e: JobEquipment) =>
  [e.manufacturer, e.model || e.part_number].filter(Boolean).join(" ") || e.category || "Unit";

function PullPill({ pulled }: { pulled: boolean }) {
  return (
    <span className={cn(
      "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
      pulled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
    )}>
      {pulled ? "Pulled" : "Not pulled"}
    </span>
  );
}

export function JobBoardOverview({ job, onClose }: { job: PendingTicket; onClose: () => void }) {
  const navigate = useNavigate();
  const jobId = job.job_id ?? null;
  const { data: full } = useJob(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: installers } = useActiveTechs("01"); // Install / Sales roster for the installer name

  const units = (equip ?? []).filter((e) => e.kind !== "accessory");
  const accessories = (equip ?? []).filter((e) => e.kind === "accessory");
  const customer = full?.customer_name ?? job.customer_name ?? job.custno;
  const address = full?.address ?? job.address;
  const installer = job.assigned_empno
    ? (installers ?? []).find((t) => t.empno === job.assigned_empno)
    : null;
  const installerName = installer
    ? (installer.firstname ? `${installer.firstname} ${installer.lastname ?? ""}`.trim() : installer.empname ?? installer.empno)
    : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-hairline px-4 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">Install</span>
            <span className="truncate text-sm font-semibold text-slate-900">{full?.title ?? "Install"} · {customer}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
            {address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{address}</span>}
            {job.scheduled_window && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />{job.scheduled_window}</span>}
            {installerName && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{installerName}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" disabled={!jobId} onClick={() => { onClose(); if (jobId) navigate(`/jobs/${jobId}`); }}>
            Open full job <ArrowRight className="h-3.5 w-3.5" />
          </Button>
          <button type="button" onClick={onClose} title="Close" className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto px-4 py-3 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <Package className="h-3.5 w-3.5" /> Equipment
            </div>
            <div className="space-y-1">
              {units.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-slate-50/60 px-2.5 py-1.5 text-xs">
                  <span className="min-w-0 truncate"><span className="font-medium text-slate-800">{unitLabel(e)}</span>{e.category ? <span className="text-slate-400"> · {e.category}</span> : null}{e.serial ? <span className="text-slate-400"> · SN {e.serial}</span> : null}</span>
                  <PullPill pulled={!!e.pulled_at} />
                </div>
              ))}
              {units.length === 0 && <p className="rounded-md border border-dashed border-hairline py-3 text-center text-xs text-slate-400">No equipment on the job yet.</p>}
            </div>
          </div>
          {accessories.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Accessories</div>
              <div className="space-y-1">
                {accessories.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-slate-50/60 px-2.5 py-1.5 text-xs">
                    <span className="min-w-0 truncate font-medium text-slate-800">{unitLabel(e)}</span>
                    <PullPill pulled={!!e.pulled_at} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="min-w-0">
          {jobId && (
            <InternalNotes
              entityType="job"
              entityId={jobId}
              title="Job & install notes"
              placeholder="Access, what to pull, gotchas…"
            />
          )}
        </div>
      </div>
    </div>
  );
}
